import React from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import Svg, { Circle, Path, Rect } from 'react-native-svg';
import { useThemeContext } from '../../../contexts';

/**
 * AcceptProofRequestAnimation is a component that displays an animation of a proof request being accepted.
 *
 * @return {React.FC}
 */
const AcceptProofRequestAnimation: React.FC = () => {
  const animatedValue = React.useRef(new Animated.Value(0)).current;
  const { theme } = useThemeContext();

  React.useEffect(() => {
    Animated.loop(
      Animated.timing(animatedValue, {
        toValue: 1,
        duration: 3000,
        easing: Easing.bezier(0.25, 1, 0.5, 1),
        useNativeDriver: false
      })
    ).start();
  }, [animatedValue]);

  const translateY = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, -80, -150]
  });

  const opacity = animatedValue.interpolate({
    inputRange: [0.7, 1],
    outputRange: [1, 0],
    extrapolate: 'clamp'
  });

  const walletScale = animatedValue.interpolate({
    inputRange: [0, 0.3, 0.6],
    outputRange: [1, 1.1, 1]
  });

  return (
    <View style={styles.container}>
      <View style={styles.animationContainer}>
        {/* Card Animation */}
        <Animated.View
          style={[
            styles.creditCard,
            {
              transform: [{ translateY }],
              opacity
            }
          ]}>
          <Svg width="100" height="60" viewBox="0 0 100 60">
            <Rect width="100" height="60" rx="8" ry="8" fill={theme.secondary} />
            <Rect x="10" y="15" width="80" height="8" fill="#FFFFFF" />
            <Rect x="10" y="35" width="50" height="5" fill="#FFFFFF" />
            <Rect x="10" y="45" width="30" height="5" fill="#FFFFFF" />
          </Svg>
        </Animated.View>

        {/* Wallet Animation */}
        <Animated.View
          style={[
            styles.wallet,
            {
              transform: [{ scale: walletScale }]
            }
          ]}>
          <Svg width="120" height="80" viewBox="0 0 120 80">
            <Rect width="120" height="80" rx="10" ry="10" fill={theme.primary} />
            <Path d="M90 35h27v15H90a7.5 7.5 0 0 1 0-15z" fill="#fff" />
            <Circle cx="90" cy="42.5" r="5" fill={theme.primary} />
          </Svg>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  animationContainer: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  creditCard: {
    position: 'absolute',
    top: 0,
    left: 10,
    zIndex: 1
  },
  wallet: {
    zIndex: 2,
    overflow: 'hidden',
    width: 120,
    height: 80
  }
});

export default AcceptProofRequestAnimation;
